import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Smartphone, PlusCircle, X, Loader2, Check } from 'lucide-react';
import { useSse } from '@/hooks/useSse';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { api, ApiError } from '@/lib/api';
import { cn, formatINR } from '@/lib/utils';

type PayMode = 'UPI_MANUAL' | 'BANK_MANUAL';
type Status = 'PENDING' | 'APPROVED' | 'COMPLETED' | 'CANCELLED' | 'TIMED_OUT' | 'DECLINED';

interface PayRequest {
  id: string;
  mode: PayMode;
  status: Status;
  beneficiaryName: string;
  beneficiaryAccount: string | null;
  beneficiaryIfsc: string | null;
  payeeUpiVpa: string | null;
  amount: string;
  purpose: string | null;
  bookingHead: string | null;
  paymentNature: string | null;
  clientName: string | null;
  requesterUserId: string;
  claimedByUserId: string | null;
  claimedAt: string | null;
  paidAt: string | null;
  createdAt: string;
}

const CLIENT_HEADS = ['FLIGHT', 'HOTEL', 'VISA', 'LAND_PACKAGE', 'INSURANCE', 'ANCILLARY'] as const;
const EXPENSE_HEADS = [
  'SALARY', 'OFFICE_RENT', 'UTILITIES', 'SOFTWARE', 'ASSET', 'SUPPLIES',
  'FOOD', 'TAX', 'COMMISSION', 'BANK_CHARGE', 'REFUND_OUT', 'INTERNAL_TRANSFER', 'ADD_BENEFICIARY', 'OTHER',
] as const;

export function PayRequestsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<PayMode>(user?.role === 'ADMIN' ? 'UPI_MANUAL' : 'BANK_MANUAL');
  const [raiseOpen, setRaiseOpen] = useState(false);

  const list = useQuery<PayRequest[]>({
    queryKey: ['pay-requests', tab],
    queryFn: () => api(`/api/pay-requests?mode=${tab}`),
    refetchInterval: 15_000,
  });

  // Live update on raise / claim / debit-match events.
  useSse({
    onEvent: (ev) => {
      if (
        ev.type === 'pay_request_pending' ||
        ev.type === 'pay_request_claimed' ||
        ev.type === 'pay_request_completed' ||
        ev.type === 'transaction_confirmed'
      ) {
        qc.invalidateQueries({ queryKey: ['pay-requests'] });
      }
    },
  });

  return (
    <div className="space-y-5">
      <PageHeader
        icon={tab === 'UPI_MANUAL' ? <Smartphone className="h-5 w-5" /> : <Banknote className="h-5 w-5" />}
        title="Pay requests"
        subtitle={
          tab === 'UPI_MANUAL'
            ? 'Ops asks any admin to UPI-pay a vendor. First admin to claim owns it; debit SMS auto-resolves.'
            : 'Ops asks accounts to NEFT/IMPS/RTGS via netbanking. First accountant to claim owns it; debit SMS auto-resolves.'
        }
        meta={
          <Button size="sm" onClick={() => setRaiseOpen(true)}>
            <PlusCircle className="mr-1.5 h-3.5 w-3.5" /> Raise pay request
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2">
        <ModeChip
          active={tab === 'BANK_MANUAL'}
          onClick={() => setTab('BANK_MANUAL')}
          icon={<Banknote className="h-3.5 w-3.5" />}
          label="Bank transfer"
          help="NEFT / IMPS / RTGS"
        />
        <ModeChip
          active={tab === 'UPI_MANUAL'}
          onClick={() => setTab('UPI_MANUAL')}
          icon={<Smartphone className="h-3.5 w-3.5" />}
          label="UPI"
          help="Pay from any UPI app"
        />
      </div>

      {raiseOpen && (
        <RaiseModal
          defaultMode={tab}
          onClose={() => setRaiseOpen(false)}
          onCreated={() => {
            setRaiseOpen(false);
            qc.invalidateQueries({ queryKey: ['pay-requests'] });
          }}
        />
      )}

      {list.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !list.data || list.data.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No {tab === 'UPI_MANUAL' ? 'UPI' : 'bank transfer'} pay requests right now.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.data.map((r) => (
            <PayRow
              key={r.id}
              row={r}
              currentUserId={user?.id}
              role={user?.role}
              onChanged={() => qc.invalidateQueries({ queryKey: ['pay-requests'] })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PayRow({
  row,
  currentUserId,
  role,
  onChanged,
}: {
  row: PayRequest;
  currentUserId: string | undefined;
  role: string | undefined;
  onChanged: () => void;
}) {
  const claim = useMutation({
    mutationFn: () => api(`/api/pay-requests/${row.id}/claim`, { method: 'POST' }),
    onSuccess: onChanged,
  });
  const release = useMutation({
    mutationFn: () => api(`/api/pay-requests/${row.id}/release`, { method: 'POST' }),
    onSuccess: onChanged,
  });

  const claimedByMe = row.claimedByUserId === currentUserId;
  const claimed = !!row.claimedByUserId;
  const isCompleted = row.status === 'COMPLETED';
  const canClaim =
    !claimed &&
    row.status === 'PENDING' &&
    ((row.mode === 'UPI_MANUAL' && role === 'ADMIN') ||
      (row.mode === 'BANK_MANUAL' && (role === 'ADMIN' || role === 'ACCOUNTANT')));

  const ageMins = Math.max(0, Math.round((Date.now() - new Date(row.createdAt).getTime()) / 60000));

  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-4 transition',
        isCompleted
          ? 'border-emerald-200 bg-emerald-50/40'
          : claimedByMe
            ? 'border-blue-300 bg-blue-50/30'
            : claimed
              ? 'border-amber-200 bg-amber-50/20'
              : 'border-rose-200 bg-rose-50/10',
      )}
    >
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-lg font-semibold tabular-nums">
              {formatINR(parseFloat(row.amount))}
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {row.mode === 'UPI_MANUAL' ? 'UPI' : 'Bank transfer'}
            </span>
            <span className="text-[10px] text-muted-foreground">{ageMins}m ago</span>
            {isCompleted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-800">
                <Check className="h-3 w-3" /> Paid · debit matched
              </span>
            )}
          </div>
          <div className="mt-1 text-base font-medium">{row.beneficiaryName}</div>
          <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {row.payeeUpiVpa && (
              <div>
                <span className="text-muted-foreground/70">UPI:</span>{' '}
                <span className="font-mono text-foreground/80">{row.payeeUpiVpa}</span>
              </div>
            )}
            {row.beneficiaryAccount && (
              <div>
                <span className="text-muted-foreground/70">A/c:</span>{' '}
                <span className="font-mono text-foreground/80">{row.beneficiaryAccount}</span>
                {row.beneficiaryIfsc && (
                  <>
                    {' · '}
                    <span className="font-mono text-foreground/80">{row.beneficiaryIfsc}</span>
                  </>
                )}
              </div>
            )}
            {row.purpose && <div>Purpose: {row.purpose}</div>}
            {(row.clientName || row.bookingHead) && (
              <div>
                {row.clientName && <span>{row.clientName}</span>}
                {row.bookingHead && <span> · {prettyHead(row.bookingHead)}</span>}
              </div>
            )}
            {claimed && !isCompleted && (
              <div className="text-amber-700">
                Claimed{claimedByMe ? ' by you' : ''} · waiting for debit
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          {canClaim && (
            <Button size="sm" onClick={() => claim.mutate()} disabled={claim.isPending}>
              {claim.isPending ? '…' : 'Claim & pay'}
            </Button>
          )}
          {claimedByMe && !isCompleted && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => release.mutate()}
              disabled={release.isPending}
            >
              {release.isPending ? '…' : 'Release'}
            </Button>
          )}
        </div>
      </div>
      {(claim.isError || release.isError) && (
        <div className="mt-2 rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
          {(claim.error ?? release.error) instanceof ApiError
            ? JSON.stringify(((claim.error ?? release.error) as ApiError).body)
            : (claim.error ?? release.error)?.toString()}
        </div>
      )}
    </div>
  );
}

function RaiseModal({
  defaultMode,
  onClose,
  onCreated,
}: {
  defaultMode: PayMode;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [mode, setMode] = useState<PayMode>(defaultMode);
  const [amount, setAmount] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [payeeUpiVpa, setPayeeUpiVpa] = useState('');
  const [beneficiaryAccount, setBeneficiaryAccount] = useState('');
  const [beneficiaryIfsc, setBeneficiaryIfsc] = useState('');
  const [paymentNature, setPaymentNature] = useState<'CLIENT_BOOKING' | 'OPERATING_EXPENSE'>(
    'CLIENT_BOOKING',
  );
  const [head, setHead] = useState<string>('');
  const [clientName, setClientName] = useState('');
  const [purpose, setPurpose] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api('/api/pay-requests', {
        method: 'POST',
        body: {
          mode,
          beneficiaryName,
          amount,
          payeeUpiVpa: mode === 'UPI_MANUAL' ? payeeUpiVpa : undefined,
          beneficiaryAccount: mode === 'BANK_MANUAL' ? beneficiaryAccount : undefined,
          beneficiaryIfsc: mode === 'BANK_MANUAL' ? beneficiaryIfsc.toUpperCase() : undefined,
          paymentNature,
          bookingHead: head || undefined,
          clientName: clientName || undefined,
          purpose: purpose || undefined,
        },
      }),
    onSuccess: onCreated,
  });

  const heads = paymentNature === 'CLIENT_BOOKING' ? CLIENT_HEADS : EXPENSE_HEADS;
  const canSubmit =
    !!amount &&
    !!beneficiaryName &&
    !!head &&
    (mode === 'UPI_MANUAL'
      ? /^[\w.+-]+@[\w.-]+$/.test(payeeUpiVpa)
      : !!beneficiaryAccount && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(beneficiaryIfsc.toUpperCase())) &&
    (paymentNature === 'OPERATING_EXPENSE' || !!clientName);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Pay requests</div>
            <div className="text-base font-semibold">Raise a pay request</div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-2">
            <SegBtn active={mode === 'UPI_MANUAL'} onClick={() => setMode('UPI_MANUAL')}>
              UPI · admin pays
            </SegBtn>
            <SegBtn active={mode === 'BANK_MANUAL'} onClick={() => setMode('BANK_MANUAL')}>
              Bank transfer · accounts pays
            </SegBtn>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <SegBtn
              active={paymentNature === 'CLIENT_BOOKING'}
              onClick={() => {
                setPaymentNature('CLIENT_BOOKING');
                setHead('');
              }}
            >
              Client booking
            </SegBtn>
            <SegBtn
              active={paymentNature === 'OPERATING_EXPENSE'}
              onClick={() => {
                setPaymentNature('OPERATING_EXPENSE');
                setHead('');
                setClientName('');
              }}
            >
              Office expense
            </SegBtn>
          </div>

          <div>
            <Label className="text-xs">Beneficiary / vendor name</Label>
            <Input
              value={beneficiaryName}
              onChange={(e) => setBeneficiaryName(e.target.value)}
              placeholder="Couvrir Roam Assist"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Amount (INR)</Label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="6186.00"
              className="mt-1"
            />
          </div>

          {mode === 'UPI_MANUAL' ? (
            <div>
              <Label className="text-xs">Payee UPI VPA</Label>
              <Input
                value={payeeUpiVpa}
                onChange={(e) => setPayeeUpiVpa(e.target.value.trim())}
                placeholder="vendor@okicici"
                className="mt-1 font-mono"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Account number</Label>
                <Input
                  value={beneficiaryAccount}
                  onChange={(e) => setBeneficiaryAccount(e.target.value.trim())}
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label className="text-xs">IFSC</Label>
                <Input
                  value={beneficiaryIfsc}
                  onChange={(e) => setBeneficiaryIfsc(e.target.value.trim())}
                  className="mt-1 font-mono uppercase"
                  placeholder="HDFC0001234"
                />
              </div>
            </div>
          )}

          {paymentNature === 'CLIENT_BOOKING' && (
            <div>
              <Label className="text-xs">Client</Label>
              <Input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Client name"
                className="mt-1"
              />
            </div>
          )}

          <div>
            <Label className="text-xs">Booking head</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {heads.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setHead(head === h ? '' : h)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition',
                    head === h
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input bg-background text-muted-foreground hover:bg-muted',
                  )}
                >
                  {prettyHead(h)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Purpose / notes (optional)</Label>
            <Input
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Trip ref, why this vendor, etc."
              className="mt-1"
            />
          </div>

          {create.isError && (
            <div className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
              {create.error instanceof ApiError
                ? JSON.stringify(create.error.body)
                : (create.error as Error).message}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border p-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSubmit || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Raising…' : 'Raise request'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModeChip({
  active,
  onClick,
  icon,
  label,
  help,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  help: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background hover:bg-muted',
      )}
    >
      {icon}
      <span className="font-medium">{label}</span>
      <span className={cn('text-xs', active ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
        · {help}
      </span>
    </button>
  );
}

function SegBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded border px-3 py-2 text-sm transition',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background text-muted-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}

function prettyHead(s: string): string {
  return s.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
